import { useState } from 'react';
import { toast } from 'react-toastify';
import { ApiError } from '/src/api';
import { toastMessages } from '/src/lib/toast';

interface UseImporterOptions {
  importerService: { import: (file: File) => Promise<{ message: string }> };
}

export const useImporter = ({ importerService }: UseImporterOptions) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith('.xlsx')) {
      toast.error(toastMessages.fileValidation.invalidExcelFile);
      return;
    }
    setSelectedFile(file);
    setValidationErrors({});
    setSuccessMessage(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error(toastMessages.fileValidation.requiredFile);
      return;
    }

    setIsUploading(true);
    setValidationErrors({});
    setSuccessMessage(null);
    try {
      const result = await importerService.import(selectedFile);
      setSuccessMessage(result.message);
      setSelectedFile(null);
      setValidationErrors({});
    } catch (error) {
      if (error instanceof ApiError) {
        try {
          const parsedBody = typeof error.body === 'string' ? JSON.parse(error.body) : error.body;
          const newValidationErrors: Record<string, string[]> = {};

          if (parsedBody?.message) {
            newValidationErrors.general = [parsedBody.message];
          }

          if (parsedBody?.errors_by_row) {
            Object.entries(parsedBody.errors_by_row).forEach(([row, errors]) => {
              newValidationErrors[row] = Array.isArray(errors) ? errors : [errors as string];
            });
          }

          setValidationErrors(newValidationErrors);
        } catch (e) {
          const message = (error.body as string) || error.message;
          setValidationErrors({ general: [message] });
          toast.error(message);
        }
      } else {
        const errorMessage = 'An error occurred while uploading the file';
        setValidationErrors({ general: [errorMessage] });
        toast.error(errorMessage);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return {
    selectedFile,
    isUploading,
    validationErrors,
    successMessage,
    handleFileSelect,
    handleUpload,
  };
};
